import template from './uc-free-product-manager.html.twig';
import './uc-free-product-manager.scss';

const { Mixin } = Shopware;
const { Criteria } = Shopware.Data;

// Sentinel option value for "+ Create new group" in the Gift group
// sw-single-select (see groupSelectOptions/onGroupSelectChange) — picking
// it switches the field to free-text entry instead of being saved as a
// literal group name itself. Namespaced/unlikely-to-collide rather than
// something like 'new', which a merchant could plausibly type as a real
// group name.
const NEW_GROUP_SENTINEL = '__uc_new_group__';

/**
 * uc-free-product-manager
 *
 * CRUD listing + form modal for the "Free products" rules
 * (sellops_uc_free_product). Modelled on uc-milestone-manager.
 */
Shopware.Component.register('uc-free-product-manager', {
    template,

    // 'acl' is injected as a service (per Shopware's own ACL docs), NOT a
    // mixin — Mixin.getByName('acl') doesn't exist and throws
    // "The mixin 'acl' is not registered." Injecting it here exposes it as
    // `this.acl` / `acl` in the template automatically, same as any other
    // injected service.
    inject: ['repositoryFactory', 'acl'],

    mixins: [
        Mixin.getByName('notification'),
    ],

    data() {
        return {
            rules: null,
            isLoading: false,
            // Distinguishes "no rules created yet" from "the list failed to
            // load" — see the same field on uc-milestone-manager for why.
            loadError: false,
            showModal: false,
            showFormBody: false,
            isNewRule: false,
            isSaving: false,
            deleteId: null,
            showDeleteConfirm: false,
            isDeleting: false,

            // UI-only — not part of the saved entity. Toggles the "Gift
            // group" field between a dropdown of existing group names and
            // a free-text field for typing a brand new one (see
            // groupSelectOptions/onGroupSelectChange below). Previously
            // this was ALWAYS a free-text field, which meant creating a
            // second rule in the same group required manually retyping —
            // and exactly matching, since it's compared as a literal
            // string — whatever was typed for the first rule. A single typo
            // silently created a second, separate group instead of joining
            // the first.
            isCreatingNewGroup: false,

            form: this._emptyForm(),
        };
    },

    computed: {
        repository() {
            return this.repositoryFactory.create('sellops_uc_free_product');
        },

        productRepository() {
            return this.repositoryFactory.create('product');
        },

        // sw-entity-multi-id-select requires a Repository instance (not an
        // entity name like sw-entity-single-select)
        salesChannelRepository() {
            return this.repositoryFactory.create('sales_channel');
        },

        /**
         * One entry per configurable product — variants picked in storefront.
         * Excludes inactive products: FreeProductGiftResolver::resolve() looks
         * the gift up via the sales-channel product repository, which only
         * ever returns active + sales-channel-assigned products — picking an
         * inactive one here would silently create a rule that can never
         * actually trigger. Matches the same active-only filter already used
         * by the Cross-Sell rule builder's product picker.
         */
        productCriteria() {
            const criteria = new Criteria(1, 25);
            criteria.addFilter(Criteria.equals('parentId', null));
            criteria.addFilter(Criteria.equals('active', true));
            return criteria;
        },

        sortedRules() {
            if (!this.rules) return [];
            return [...this.rules].sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0));
        },

        /**
         * How many rules currently share each non-empty group_id — used to
         * show "2 in this group" on the list and to know which sibling rows
         * need their maxSelectable kept in sync when one is edited (see
         * _syncMaxSelectableAcrossGroup).
         */
        groupCounts() {
            const counts = {};
            (this.rules ?? []).forEach((r) => {
                if (!r.groupId) return;
                counts[r.groupId] = (counts[r.groupId] ?? 0) + 1;
            });
            return counts;
        },

        /** Every distinct, non-empty group name already used by another rule, A→Z. */
        existingGroupNames() {
            const names = new Set();
            (this.rules ?? []).forEach((r) => {
                if (r.groupId) names.add(r.groupId);
            });
            return [...names].sort((a, b) => a.localeCompare(b));
        },

        /**
         * Options for the "Gift group" sw-single-select — every existing
         * group name, plus a sentinel "+ Create new group" entry at the
         * end. onGroupSelectChange() below is what gives that sentinel
         * value special handling instead of it being savable as a literal
         * group name.
         */
        groupSelectOptions() {
            const options = this.existingGroupNames.map((name) => ({ value: name, label: name }));
            options.push({
                value: NEW_GROUP_SENTINEL,
                label: this.$tc('sellops-ultimate-cart.freeProducts.fieldGroupCreateNew'),
            });
            return options;
        },

        // Built-in default icon for free-product gift rules — mirrors the
        // same free-gift.svg the storefront falls back to for the
        // 'free_product' reward type (see ProgressBarResolver /
        // progress-bar.html.twig), so the popup shows the same image
        // shoppers would actually see before the merchant uploads their own.
        freeProductDefaultIconUrl() {
            // See the matching note in uc-milestone-manager's
            // milestoneDefaultIconUrl() — assetsPath already ends in
            // '/bundles/', so no 'bundles/' prefix is added here.
            const assetFilter = Shopware.Filter.getByName('asset');
            return assetFilter('sellopsultimatecart/img/milestones/free-gift.png');
        },

        // Celebration animation played once, client-side, the moment a
        // shopper unlocks this gift — mirrors uc-milestone-manager's own
        // animationTypeOptions. See docs/celebration-animations-architecture.md.
        animationTypeOptions() {
            return [
                { value: 'none',      label: this.$tc('sellops-ultimate-cart.freeProducts.animationType.none') },
                { value: 'confetti',  label: this.$tc('sellops-ultimate-cart.freeProducts.animationType.confetti') },
                { value: 'fireworks', label: this.$tc('sellops-ultimate-cart.freeProducts.animationType.fireworks') },
                { value: 'gift',      label: this.$tc('sellops-ultimate-cart.freeProducts.animationType.gift') },
                { value: 'premium',   label: this.$tc('sellops-ultimate-cart.freeProducts.animationType.premium') },
            ];
        },
    },

    created() {
        this.loadRules();
    },

    methods: {
        _emptyForm() {
            return {
                id:              null,
                active:          true,
                oncePerCustomer: false,
                activeFrom:      null,
                activeUntil:     null,
                salesChannelIds: [],
                productId:           null,
                iconUrl:             null,
                labelBefore:         '',
                labelAfter:          '',
                animationType:       'none',
                cartValue:           50,
                priority:            0,
                // Empty string, not null — sw-text-field round-trips '' more
                // predictably than null, and '' already reads as "ungrouped"
                // everywhere this is checked (isGrouped() mirrors that on
                // the PHP side).
                groupId:             '',
                maxSelectable:       1,
            };
        },

        async loadRules() {
            this.isLoading = true;
            this.loadError = false;
            try {
                const criteria = new Criteria();
                criteria.addSorting(Criteria.sort('priority', 'DESC'));
                this.rules = await this.repository.search(criteria, Shopware.Context.api);
                await this._loadProductNames();
            } catch (e) {
                console.error('[UltimateCart] Failed to load free product rules:', e);
                this.rules = null;
                this.loadError = true;
            } finally {
                this.isLoading = false;
            }
        },

        /** Resolve product names for the listing */
        async _loadProductNames() {
            if (!this.rules || this.rules.length === 0) return;
            const ids = [...new Set(this.rules.map(r => r.productId).filter(Boolean))];
            if (!ids.length) return;
            try {
                const products = await this.productRepository.search(
                    new Criteria(1, ids.length).setIds(ids),
                    Shopware.Context.api
                );
                const names = {};
                products.forEach(p => { names[p.id] = p.translated?.name ?? p.name; });
                this.rules.forEach(r => { r.productName = names[r.productId] ?? '—'; });
            } catch {
                // listing falls back to showing the id
            }
        },

        openCreate() {
            this.form = this._emptyForm();
            this.isNewRule = true;
            // Start on the dropdown when there's something to pick from —
            // that's the common case this whole feature exists for (join
            // an existing group). Straight to free-text entry when there
            // are no groups yet at all, since the dropdown would only ever
            // show the "+ Create new group" option anyway.
            this.isCreatingNewGroup = this.existingGroupNames.length === 0;
            this.showFormBody = true;
            this.showModal = true;
        },

        openEdit(rule) {
            this.form = {
                id:              rule.id,
                active:          rule.active,
                oncePerCustomer: rule.oncePerCustomer,
                activeFrom:      rule.activeFrom,
                activeUntil:     rule.activeUntil,
                salesChannelIds: rule.salesChannelIds ?? [],
                productId:           rule.productId,
                iconUrl:             rule.iconUrl ?? null,
                labelBefore:         rule.labelBefore ?? '',
                labelAfter:          rule.labelAfter ?? '',
                animationType:       rule.animationType ?? 'none',
                cartValue:           rule.cartValue,
                priority:            rule.priority,
                groupId:             rule.groupId ?? '',
                maxSelectable:       rule.maxSelectable ?? 1,
            };

            // Editing a rule that's already grouped: show the dropdown,
            // pre-selected to its current group (it's necessarily one of
            // existingGroupNames already, since it came from this.rules).
            // Editing an ungrouped rule: same "dropdown if there's
            // anything to pick, else straight to text entry" rule as
            // openCreate().
            this.isCreatingNewGroup = !rule.groupId && this.existingGroupNames.length === 0;

            this.isNewRule = false;
            this.showFormBody = true;
            this.showModal = true;
        },

        /**
         * Handles a selection from the Gift group dropdown. Picking the
         * "+ Create new group" sentinel switches the field over to free-
         * text entry (see isCreatingNewGroup in the template) instead of
         * being saved as a literal group name — everything else is a real
         * existing group name, applied directly.
         */
        onGroupSelectChange(value) {
            if (value === NEW_GROUP_SENTINEL) {
                this.isCreatingNewGroup = true;
                this.form.groupId = '';
            } else {
                this.isCreatingNewGroup = false;
                this.form.groupId = value;
            }
        },

        /** "← choose an existing group instead" link under the free-text field. */
        chooseExistingGroup() {
            this.isCreatingNewGroup = false;
            this.form.groupId = '';
        },

        /**
         * Tear down selects first (their result lists teleport to <body>),
         * then unmount the modal. Doing both at once causes Vue insertBefore
         * errors; v-show leaves orphaned dropdowns.
         */
        async _closeFormModal() {
            if (document.activeElement instanceof HTMLElement) {
                document.activeElement.blur();
            }

            this.showFormBody = false;
            await this.$nextTick();
            await this.$nextTick();

            this.showModal = false;
            await this.$nextTick();
        },

        async saveRule() {
            if (!this.form.productId) {
                this.createNotificationError({
                    message: this.$tc('sellops-ultimate-cart.freeProducts.productRequired'),
                });
                return;
            }

            this.isSaving = true;
            try {
                let entity;
                if (this.isNewRule) {
                    entity = this.repository.create(Shopware.Context.api);
                } else {
                    entity = await this.repository.get(this.form.id, Shopware.Context.api);
                }

                // id is excluded from the copy on purpose (entity already
                // owns its own id) — delete instead of destructuring it into
                // an unused binding, so no eslint-disable comment is needed
                // at all (different lint configs across shopware-cli's build
                // pipeline and its `extension validate` command disagree on
                // whether the `@typescript-eslint/no-unused-vars` rule name
                // is even registered, so no single disable comment satisfies
                // both).
                const formData = { ...this.form };
                delete formData.id;
                // Store NULL, not '', for "ungrouped" — matches isGrouped()
                // on the PHP side (empty string would otherwise still count
                // as "grouped" there) and keeps the DB index over group_id
                // meaningful.
                formData.groupId = formData.groupId ? formData.groupId.trim() : null;
                if (!formData.groupId) {
                    formData.maxSelectable = 1;
                }
                Object.assign(entity, formData);
                await this.repository.save(entity, Shopware.Context.api);

                if (formData.groupId) {
                    await this._syncMaxSelectableAcrossGroup(formData.groupId, formData.maxSelectable, entity.id);
                }

                await this._closeFormModal();
                await this.loadRules();

                // Defer growl — also teleports to <body>; same flush as modal
                // teardown can throw insertBefore
                window.setTimeout(() => {
                    this.createNotificationSuccess({
                        message: this.$tc('sellops-ultimate-cart.freeProducts.saveSuccess'),
                    });
                }, 50);
            } catch (e) {
                console.error('[UltimateCart] Failed to save free product rule:', e);
                this.createNotificationError({
                    message: this.$tc('sellops-ultimate-cart.freeProducts.saveError'),
                });
            } finally {
                this.isSaving = false;
            }
        },

        async cancelModal() {
            await this._closeFormModal();
        },

        confirmDelete(id) {
            this.deleteId = id;
            this.showDeleteConfirm = true;
        },

        cancelDelete() {
            this.deleteId = null;
            this.showDeleteConfirm = false;
        },

        async executeDelete() {
            if (!this.deleteId) return;
            this.isDeleting = true;
            const idToDelete = this.deleteId;
            try {
                await this.repository.delete(idToDelete, Shopware.Context.api);

                // Close the confirm modal before the growl fires (teleport clash)
                this.isDeleting = false;
                this.deleteId = null;
                this.showDeleteConfirm = false;
                await this.$nextTick();
                await this.loadRules();

                this.createNotificationSuccess({
                    message: this.$tc('sellops-ultimate-cart.freeProducts.deleteSuccess'),
                });
            } catch (e) {
                console.error('[UltimateCart] Failed to delete free product rule:', e);
                this.isDeleting = false;
                this.deleteId = null;
                this.showDeleteConfirm = false;
                this.createNotificationError({
                    message: this.$tc('sellops-ultimate-cart.freeProducts.deleteError'),
                });
            }
        },

        /**
         * Every rule sharing a group_id must agree on max_selectable — the
         * resolver reads whichever row it loads first and treats that as
         * "the" group cap (see FreeProductGiftResolver), so a mismatch would
         * silently produce a different cap depending on load order. Rather
         * than ask the merchant to manually keep N rows in sync, saving one
         * rule with a group_id pushes its max_selectable value onto every
         * other rule already in that group.
         */
        async _syncMaxSelectableAcrossGroup(groupId, maxSelectable, savedRuleId) {
            const siblings = (this.rules ?? []).filter(
                (r) => r.groupId === groupId && r.id !== savedRuleId && r.maxSelectable !== maxSelectable
            );
            if (!siblings.length) return;

            try {
                const toSync = await Promise.all(siblings.map(async (r) => {
                    const entity = await this.repository.get(r.id, Shopware.Context.api);
                    entity.maxSelectable = maxSelectable;
                    return entity;
                }));
                await Promise.all(toSync.map((entity) => this.repository.save(entity, Shopware.Context.api)));
            } catch (e) {
                console.error('[UltimateCart] Failed to sync maxSelectable across group siblings:', e);
                // Non-fatal — the just-saved rule itself is already correct;
                // a stale sibling will simply get corrected the next time
                // any rule in the group is saved.
            }
        },

        /**
         * "3 in this group, pick up to 2" style label for the list table.
         *
         * Bug fix, take 2: the first attempt renamed the interpolated
         * `count` value to `total` (suspecting a collision with $tc's own
         * pluralization "choice" argument, which vue-i18n implicitly
         * exposes as `count`/`n`). That didn't fix it — after rebuilding,
         * the badge went from showing a wrong "0" to showing NO numbers at
         * all, which points at the real problem: $tc's third argument
         * (`...values`) does not reliably resolve NAMED placeholders like
         * `{total}`/`{max}` in this vue-i18n setup — only $t's second
         * argument does. $tc's choice-based special variable (count/n) was
         * the ONLY thing actually rendering before, which is why the
         * output changed from "0" to blank once nothing was named `count`
         * anymore. Switched to $t — no pluralization forms are needed
         * here anyway (this string never varies by count), so $tc was
         * never the right call to begin with.
         */
        groupLabel(rule) {
            if (!rule.groupId) return null;
            const total = this.groupCounts[rule.groupId] ?? 1;
            return this.$t('sellops-ultimate-cart.freeProducts.groupBadge', {
                total,
                max: rule.maxSelectable ?? 1,
            });
        },

        validityLabel(rule) {
            const from  = rule.activeFrom  ? new Date(rule.activeFrom).toLocaleDateString()  : '…';
            const until = rule.activeUntil ? new Date(rule.activeUntil).toLocaleDateString() : '…';
            if (!rule.activeFrom && !rule.activeUntil) {
                return this.$tc('sellops-ultimate-cart.freeProducts.alwaysValid');
            }
            return `${from} – ${until}`;
        },
    },
});
